import { Platform } from 'react-native';
import { functions, httpsCallable, getIdToken } from '../config/firebase';

// Use environment variable explicitly; default to production
const useLocalFunctions = process.env.EXPO_PUBLIC_USE_LOCAL_FUNCTIONS === 'true';

// API URLs
const LOCAL_API_URL = 'http://127.0.0.1:5001/habitcoach-9c912/us-central1';
const PRODUCTION_API_URL = 'https://us-central1-habitcoach-9c912.cloudfunctions.net';

export const API_BASE_URL = useLocalFunctions ? LOCAL_API_URL : PRODUCTION_API_URL;

console.log('Firebase Functions Config:', {
  NODE_ENV: process.env.NODE_ENV,
  USE_LOCAL_FUNCTIONS: process.env.EXPO_PUBLIC_USE_LOCAL_FUNCTIONS,
  API_URL: API_BASE_URL
});

export abstract class FirebaseFunctionsAPI {
  protected async getAuthToken(): Promise<string | null> {
    try {
      // Use the centralized Firebase config
      // Use require to ensure Firebase is initialized first
      let auth;
      try {
        const firebaseConfig = require('../config/firebase');
        auth = firebaseConfig.auth;
      } catch (e) {
        console.warn('Failed to load firebase config in getAuthToken:', e);
        return null;
      }
      
      if (!auth) {
        console.warn('Firebase auth not initialized');
        return null;
      }
      
      // Get current user (works for both web and native)
      const user = auth.currentUser;
      
      // Allow unauthenticated for local development
      if (useLocalFunctions && !user) {
        return null;
      }
      
      if (!user) {
        throw new Error('User must be authenticated');
      }
      
      // Get ID token using modular API (works for both platforms)
      return await getIdToken(user);
    } catch (error) {
      console.error('Error getting auth token:', error);
      throw error;
    }
  }

  protected async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  protected getCallableUrl(functionName: string): string {
    return `${API_BASE_URL}/${functionName}`;
  }

  protected async makeRequest<T>(
    endpoint: string,
    data: any
  ): Promise<T> {
    try {
      // Use Firebase Functions SDK for callable functions
      if (!functions) {
        throw new Error('Firebase Functions not initialized');
      }
      
      console.log(`Making callable request to ${endpoint}`);
      
      // Platform-agnostic callable function
      const callableFunction = httpsCallable(functions, endpoint);
      const result = await callableFunction(data);
      
      console.log(`Callable function ${endpoint} response:`, result);
      
      return (Platform.OS === 'web' ? result.data : result.data) as T;
      
    } catch (error: any) {
      console.error(`Firebase Function Error (${endpoint}):`, error);
      
      // Handle Firebase Functions specific errors
      if (error.code === 'functions/unauthenticated') {
        throw new Error('Authentication required. Please log in again.');
      } else if (error.code === 'functions/permission-denied') {
        throw new Error('Permission denied. Please check your access rights.');
      } else if (error.code === 'functions/not-found') {
        throw new Error(`Function ${endpoint} not found.`);
      }
      
      throw error;
    }
  }
} 